import React, { useState } from "react";
import { API_URL, getAuthHeaders } from "../utils/api";

const IMPORTANCE_LABELS = {
  1: "Nice to have – very low priority",
  2: "Minor improvement",
  3: "Somewhat useful",
  4: "Would improve workflow",
  5: "Medium value",
  6: "Regularly needed",
  7: "High value – would save significant time",
  8: "Very important to daily operations",
  9: "Critical to business goals",
  10: "Must have – blocking progress",
};

function FeatureRequestModal({ onClose }) {
  const [form, setForm] = useState({
    location_description: "",
    description: "",
    priority: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location_description.trim() || !form.description.trim() || !form.priority) {
      setError("Please fill out all fields.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/admin/help-submissions`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ type: "feature", ...form }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(data.error || "Failed to submit. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content help-submission-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>💡 Request a Feature</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {submitted ? (
          <div className="modal-body submission-success">
            <div className="submission-success-icon">✅</div>
            <h3>Feature request submitted!</h3>
            <p>Thanks for the idea! Our team will review it and consider it for a future update.</p>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="form-error-banner">{error}</div>}

              <div className="form-group">
                <label htmlFor="feature-location">Where should this feature be? *</label>
                <input
                  id="feature-location"
                  type="text"
                  placeholder="e.g. Orders page, Customer profile, Scheduling view..."
                  value={form.location_description}
                  onChange={(e) => setForm({ ...form, location_description: e.target.value })}
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label htmlFor="feature-description">Describe the feature *</label>
                <textarea
                  id="feature-description"
                  placeholder="What should the feature do? Why is it needed? How would it improve your workflow?"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={7}
                  className="form-textarea-long"
                />
              </div>

              <div className="form-group">
                <label htmlFor="feature-importance">
                  Importance *
                  {form.priority && (
                    <span className="priority-hint"> — {IMPORTANCE_LABELS[form.priority]}</span>
                  )}
                </label>
                <div className="priority-selector">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`priority-btn ${parseInt(form.priority) === n ? "selected" : ""} priority-color-${n}`}
                      onClick={() => setForm({ ...form, priority: n })}
                      title={IMPORTANCE_LABELS[n]}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="priority-scale-labels">
                  <span>1 = Nice to have</span>
                  <span>10 = Must have</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit Feature Request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default FeatureRequestModal;
